import React, { FunctionComponent, useState } from 'react';
import CheckCircleIcon from 'src/shared/material_icons/action/check_circle.svg';
import ChecklistIcon from 'src/shared/material_icons/editor/checklist.svg';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import RadioButtonUncheckedIcon from 'src/shared/material_icons/toggle/radio_button_unchecked.svg';
import styled from 'styled-components';
import { Icon } from '../icon';
import { T } from './helpers';

const TODO_TOTAL_STEPS = 6;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    overflow: hidden;
`;

const CardHeader = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    text-align: left;
    transition: background 0.12s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.02);
    }
`;

const CardIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    border-radius: 6px;
    background: #cfd8dc;
    color: #455a64;
`;

const CardHeaderIcon = styled(Icon)`
    width: 16px;
    height: 16px;
    min-width: 16px;
`;

const CardInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const CardTitle = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #263238;
    line-height: 1.3;
`;

const CardSubtitle = styled.div`
    font-size: 11px;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.3;
`;

const CardArrow = styled(Icon)<{ $expanded: boolean }>`
    color: #9ca3af;
    width: 16px;
    height: 16px;
    min-width: 16px;
    transform: rotate(${(props) => (props.$expanded ? '180deg' : '0deg')});
    transition: transform 0.2s ease;
`;

const ProgressTrack = styled.div`
    display: grid;
    grid-template-columns: repeat(${TODO_TOTAL_STEPS}, 1fr);
    gap: 3px;
    padding: 0 10px 8px;
`;

const ProgressSegment = styled.div<{ $completed: boolean }>`
    height: 3px;
    border-radius: 2px;
    background: ${(props) => (props.$completed ? '#E8650A' : '#e5e7eb')};
    transition: background 0.2s ease;
`;

const StepsContainer = styled.div<{ $expanded: boolean }>`
    display: ${(props) => (props.$expanded ? 'flex' : 'none')};
    flex-direction: column;
    border-top: 1px solid #e5e7eb;
`;

const StepRow = styled.a`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    text-decoration: none;
    transition: background 0.12s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.02);
    }

    & + & {
        border-top: 1px solid #f3f4f6;
    }
`;

const StepCheck = styled.span<{ $completed: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    color: ${(props) => (props.$completed ? '#E8650A' : '#d1d5db')};
`;

const StepCheckIcon = styled(Icon)`
    width: 16px;
    height: 16px;
    min-width: 16px;
`;

const StepLabel = styled.span<{ $completed: boolean }>`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: ${(props) => (props.$completed ? '#9ca3af' : '#374151')};
    text-decoration: ${(props) => (props.$completed ? 'line-through' : 'none')};
    line-height: 1.3;
`;

const CardFooter = styled.div`
    display: flex;
    padding: 6px 12px 8px;
    border-top: 1px solid #e5e7eb;
`;

const FooterLink = styled.a`
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    text-decoration: none;

    &:hover {
        color: #374151;
        text-decoration: underline;
    }
`;

interface Step {
    completed: boolean;
    text: string;
    url?: string;
}

interface ToDoListProps {
    steps: Step[];
    numCompleted: number;
}

const ToDoList: FunctionComponent<ToDoListProps> = ({ steps, numCompleted }) => {
    const [expanded, setExpanded] = useState(false);

    if (numCompleted >= TODO_TOTAL_STEPS) return null;

    return (
        <Card>
            <CardHeader type='button' onClick={() => setExpanded(!expanded)}>
                <CardIcon>
                    <CardHeaderIcon aria-hidden='true'>
                        <ChecklistIcon />
                    </CardHeaderIcon>
                </CardIcon>
                <CardInfo>
                    <CardTitle>{T._('To do list', 'Admin')}</CardTitle>
                    <CardSubtitle>
                        {numCompleted} {T._('of', 'Admin')} {TODO_TOTAL_STEPS} {T._('completed', 'Admin')}
                    </CardSubtitle>
                </CardInfo>
                <CardArrow $expanded={expanded} aria-hidden='true'>
                    <ExpandMoreIcon />
                </CardArrow>
            </CardHeader>
            <ProgressTrack>
                {Array.from({ length: TODO_TOTAL_STEPS }, (_, i) => (
                    <ProgressSegment key={i} $completed={i < numCompleted} />
                ))}
            </ProgressTrack>
            <StepsContainer $expanded={expanded}>
                {steps.map((step, i) => {
                    const StepIcon = step.completed ? CheckCircleIcon : RadioButtonUncheckedIcon;

                    return (
                        <StepRow key={i} href={step.url} target='_blank' onClick={(e) => !step.url && e.preventDefault()}>
                            <StepCheck $completed={step.completed}>
                                <StepCheckIcon aria-hidden='true'>
                                    <StepIcon />
                                </StepCheckIcon>
                            </StepCheck>
                            <StepLabel $completed={step.completed}>{step.text}</StepLabel>
                        </StepRow>
                    );
                })}
                <CardFooter>
                    <FooterLink href='https://zakeke.zendesk.com/hc/en-us' target='_blank'>
                        {T._('Learn more', 'Admin')}
                    </FooterLink>
                </CardFooter>
            </StepsContainer>
        </Card>
    );
};

export default ToDoList;
